import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getLicensePlans, changeLicense } from "../../services/usageStatsService";

const PlanCard = ({ plan, isCurrent, busy, onPick }) => (
  <Box
    sx={(th) => ({
      p: 2,
      borderRadius: 3,
      border: "2px solid",
      borderColor: isCurrent ? "primary.main" : "divider",
      bgcolor: isCurrent ? alpha(th.palette.primary.main, 0.06) : "background.paper",
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
      height: "100%",
    })}
  >
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.1 }}>{plan.label || plan.code}</Typography>
      {isCurrent && <Chip size="small" color="primary" icon={<CheckCircleIcon />} label="Aktuálny" />}
    </Box>
    <Typography variant="h6" sx={{ fontWeight: 800, color: "primary.main" }}>{plan.price_label}</Typography>
    <Typography variant="body2" color="text.secondary">{plan.access}</Typography>
    <Typography variant="body2" color="text.secondary">{plan.docs}</Typography>
    {plan.note && <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>{plan.note}</Typography>}
    <Box sx={{ flex: 1 }} />
    <Button
      size="small"
      variant={isCurrent ? "outlined" : "contained"}
      disableElevation
      disabled={isCurrent || busy || plan.individual}
      onClick={() => onPick(plan.code)}
      sx={{ mt: 1, borderRadius: 2, fontWeight: 700 }}
    >
      {isCurrent ? "Používate" : plan.individual ? "Na vyžiadanie u správcu" : "Požiadať o zmenu"}
    </Button>
  </Box>
);

const LicensePlansDialog = ({ open, currentProgram, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setError(""); setData(null);
    getLicensePlans()
      .then(setData)
      .catch((e) => setError(e.message || "Chyba"))
      .finally(() => setLoading(false));
  }, [open]);

  const current = data?.current || currentProgram || null;
  const groups = data?.group_labels || {};
  const plansByGroup = (data?.plans || []).reduce((acc, p) => {
    (acc[p.group] = acc[p.group] || []).push(p);
    return acc;
  }, {});

  const handlePick = async (code) => {
    setBusy(code); setError("");
    try {
      const res = await changeLicense(code);
      onChanged && onChanged(res);
      onClose && onClose();
    } catch (e) {
      setError(e.message || "Zmena zlyhala.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Licenčné programy
        <Typography variant="body2" color="text.secondary">Cenník ISIT CENZOR — vyberte program a požiadajte o zmenu (schvaľuje správca).</Typography>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={{ bgcolor: "background.default" }}>
        {loading && <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
        {data && Object.keys(plansByGroup).map((g) => (
          <Box key={g} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}>
              {groups[g] || g}
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }, gap: 1.5 }}>
              {plansByGroup[g].map((p) => (
                <PlanCard key={p.code} plan={p} isCurrent={current === p.code} busy={!!busy} onPick={handlePick} />
              ))}
            </Box>
          </Box>
        ))}
        <Typography variant="caption" color="text.secondary">
          Ceny sú koncové, mesačný nájom je úmerný počtu prístupov. Zmena platí pre celú vašu organizáciu.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={!!busy} sx={{ fontWeight: 600 }}>Zavrieť</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LicensePlansDialog;
